import { Box, Button, Typography } from '@mui/material';
import { i18n } from 'i18next';
import React from 'react';

import { flexMixin } from 'utils/flexMixin';
import { getValueByPath } from 'utils/objectWithStringPath';

import { UseCaseTableFields } from 'core/enums/common';

import { EditConfig } from 'components/TicketSteps/UsecasesStep/EditUsecaseStep';
import { ExpandableTextField } from 'components/shared/ExpandableTextField';

import { ReactComponent as Refresh } from 'assets/icons/refresh.svg';
import { regularTextColor, titleTextColor } from 'assets/styles/_palette';

interface ITranslationConfig {
	instance: i18n;
	translate: (key: string) => string;
}

interface IPathData {
	categoryName: UseCaseTableFields;
	firstLevelIndex?: number | null;
	firstLevelKey?: string | null;
	secondLevelIndex?: number | null;
	secondLevelKey?: string | null;
}

const handleSubListsNumbering = (array: Array<any>): Array<any> => {
	let mainIndex = 0;
	let subIndex = 0;

	return array.map((item) => {
		if (typeof item !== 'string') return item;

		if (item.trim().match(/^\d+\.\d+\./)) {
			return `${mainIndex}.${++subIndex} ${item.trim().replace(/^\d+\.\d+\.\s*/, '')}`;
		} else if (item.trim().match(/^\d+\./)) {
			return `${mainIndex}.${++subIndex} ${item.trim().replace(/^\d+\.\s*/, '')}`;
		} else {
			mainIndex++;
			subIndex = 0;
			return `${mainIndex}. ${item}`;
		}
	});
};

export const generateUseCaseDescriptionJSX = (
	tableObj: Record<UseCaseTableFields, string>,
	handleTableItemDelete?: (path: string) => void,
	handleTableItemRefresh?: (path: string) => void,
	editConfig?: EditConfig | undefined,
	translationConfig?: ITranslationConfig
) => {
	if (!Object.keys(tableObj).length) return null;

	return (
		<Box
			sx={{
				display: 'flex',
				flexDirection: 'column',
				gap: '16px',
				margin: '12px 0 24px',
			}}
		>
			{(Object.entries(tableObj) as Array<[UseCaseTableFields, string]>).map(
				([key, value], index) => (
					<Box
						data-testid={key}
						className="rtl-able"
						sx={{
							display: 'flex',
							flexDirection: 'column',
							gap: '8px',
						}}
						key={index}
					>
						<Typography
							sx={{
								fontWeight: 700,
								color: titleTextColor,
							}}
						>
							{translationConfig?.translate(`descriptionTable.${key}`)}
						</Typography>
						{parseTableCellValue(
							value,
							{
								categoryName: key,
								firstLevelIndex: null,
								firstLevelKey: null,
								secondLevelIndex: null,
								secondLevelKey: null,
							},
							handleTableItemDelete,
							handleTableItemRefresh,
							editConfig,
							translationConfig
						)}
					</Box>
				)
			)}
		</Box>
	);
};

// recsively parse table object, creating/updating pathData object with each iteration.
export const parseTableCellValue = (
	cellValue: any,
	pathData: IPathData,
	handleTableItemDelete?: (path: string) => void,
	handleTableItemRefresh?: (path: string) => void,
	editConfig?: EditConfig | undefined,
	translationConfig?: ITranslationConfig
): React.ReactElement | null => {
	let path: any = '';

	if (
		pathData.categoryName === UseCaseTableFields['Key Roles'] &&
		pathData.firstLevelIndex !== null &&
		pathData.secondLevelKey !== null
	) {
		path = `tables.${0}.table.${pathData.categoryName}.${pathData.firstLevelIndex}.${
			pathData.secondLevelKey
		}.value`;
	}

	if (
		pathData.categoryName !== UseCaseTableFields['Key Roles'] &&
		pathData.firstLevelIndex !== null
	) {
		path = `tables.${0}.table.${pathData.categoryName}.${pathData.firstLevelIndex}.value`;
	}

	// if value is string, display it as text. In case if editMode is true, display it as input
	if (
		typeof cellValue === 'string' ||
		(typeof cellValue === 'object' &&
			!Array.isArray(cellValue) &&
			cellValue !== null &&
			Object.keys(cellValue).includes('isDeleted'))
	)
		return (
			<Box
				sx={{
					display: 'flex',
					flexDirection: 'column',
				}}
			>
				{editConfig && path ? (
					<Box
						sx={{
							...flexMixin({
								justify: 'flex-start',
								align: 'flex-start',
								direction: 'column',
							}),
						}}
					>
						<Box
							sx={{
								...flexMixin({
									justify: 'flex-end',
								}),
								width: '100%',
							}}
						>
							{cellValue && handleTableItemDelete && handleTableItemRefresh ? (
								!(
									path.includes(UseCaseTableFields['Key Roles']) &&
									path.includes('Responsibilities')
								) ? (
									cellValue?.isDeleted ? (
										<Button
											type="button"
											sx={{
												minWidth: '24px',
												height: '24px',
												fontSize: '24px',
												color: '#9FA6B3',
											}}
											onClick={() => handleTableItemRefresh(path)}
										>
											<Refresh />
										</Button>
									) : (
										<Button
											type="button"
											sx={{
												minWidth: '24px',
												height: '24px',
												fontSize: '24px',
												color: '#9FA6B3',
											}}
											onClick={() => handleTableItemDelete(path)}
										>
											<span>&times;</span>
										</Button>
									)
								) : null
							) : null}
						</Box>
						<ExpandableTextField
							errors={getValueByPath(editConfig.errorsInstance, `${path}`)}
							registerConfig={editConfig.registerInstance(path)}
							isDisabled={cellValue?.isDeleted}
							defaultValue={cellValue.value}
						/>
					</Box>
				) : (
					<Typography
						className="rtl-able"
						sx={{ fontSize: '0.95rem', color: regularTextColor }}
						key={cellValue}
					>
						{cellValue}
					</Typography>
				)}
			</Box>
		);

	// if value is array, display it as numbered list
	if (Array.isArray(cellValue)) {
		return (
			<>
				{handleSubListsNumbering(cellValue).map((value, index) => {
					let modifiedPathData = { ...pathData };

					if (pathData.firstLevelKey) modifiedPathData = { ...pathData, secondLevelIndex: index };
					if (!pathData.firstLevelKey) modifiedPathData = { ...pathData, firstLevelIndex: index };

					return (
						<Box key={index}>
							{parseTableCellValue(
								value,
								modifiedPathData,
								handleTableItemDelete,
								handleTableItemRefresh,
								editConfig,
								translationConfig
							)}
						</Box>
					);
				})}
			</>
		);
	}

	// if value is object. Display is in such format:
	// key:
	// value
	if (
		typeof cellValue === 'object' &&
		!Array.isArray(cellValue) &&
		cellValue !== null &&
		!Object.keys(cellValue).includes('isDeleted')
	) {
		return (
			<>
				{Object.entries(cellValue).map(([key, value]) => {
					let modifiedPathData = { ...pathData };

					if (pathData.categoryName === UseCaseTableFields['Key Roles'])
						modifiedPathData = { ...pathData, secondLevelKey: key };

					return key === 'value' ? (
						<>
							{parseTableCellValue(
								value,
								modifiedPathData,
								handleTableItemDelete,
								handleTableItemRefresh,
								editConfig,
								translationConfig
							)}
						</>
					) : (
						<Box
							sx={{
								paddingBottom: '8px',
							}}
							key={key}
							data-test={key}
						>
							<Typography
								className="rtl-able"
								sx={{ fontSize: '1rem', fontWeight: 700, color: regularTextColor }}
							>{`${translationConfig?.translate(`descriptionTable.${key}`)}:`}</Typography>
							{parseTableCellValue(
								value,
								modifiedPathData,
								handleTableItemDelete,
								handleTableItemRefresh,
								editConfig,
								translationConfig
							)}
						</Box>
					);
				})}
			</>
		);
	}

	return null;
};
